import {isSecondaryManual,manualLauncherAngle} from './componentManualLauncherAssembly'
// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import {
  AdditiveBlending, BufferGeometry, Color, DataTexture, Group, InstancedMesh, LinearFilter, Matrix4, Mesh,
  MeshBasicMaterial, MeshStandardMaterial, PlaneGeometry, RGBAFormat, SphereGeometry, UnsignedByteType,
  Vector3, type Material, type PointLight,
} from 'three'
import type { LoadedMachine } from '@core/machine'
import type { DropTarget } from '@core/sim/devices/DropTarget'
import type { Magnet } from '@core/sim/devices/Magnet'
import {componentLampKey} from './componentLamp'
import {isScoopKicker,kickerEjectStroke} from './componentKickerPresentation'
import {autoLauncherAngle} from './autoLauncherPresentation'
import type { BackglassSurface } from './cabinet'
import { componentLightLayout, type ComponentLightSite } from './componentLightLayout'
import { ComponentLightingState, createLightingSample } from './componentLightingState'
import { lampBrightness } from './lampStates'
import type { LampStateReader } from './lamps'

interface Binding { readonly owner: string; readonly lamp: string; readonly material: MeshStandardMaterial; readonly original: Color; readonly gain: number; readonly mesh: Mesh; readonly source: MeshStandardMaterial; readonly magnet: boolean }
interface CapLens { readonly mesh: Mesh; readonly site: number; readonly centre: Vector3; readonly radius: number }
export interface BuiltComponentLights {
  readonly group: Group; readonly sites: readonly ComponentLightSite[]; readonly materials: readonly Material[]
  readonly geometries: readonly BufferGeometry[]; readonly texture: DataTexture
  readonly update: (state: ComponentLightingState | undefined, read: LampStateReader | undefined, step: number, reduced: boolean, light?: PointLight) => void
  readonly dispose: () => void
  readonly lift: (id: string | undefined) => void
}

/** Two batched draws for all emitters and soft local pools. Individual hardware keeps its printed finish. */
export function buildComponentLights(machine: LoadedMachine, hardware: Group, backglass?: BackglassSurface, reuse?: Group): BuiltComponentLights {
  const sites = componentLightLayout(machine.definition, machine), group = reuse ?? new Group()
  const slings=new Set(machine.definition.devices.filter(d=>d.kind==='slingshot').map(d=>d.id))
  const bumpers=new Set(machine.definition.devices.filter(d=>d.kind==='popBumper').map(d=>d.id))
  for(const d of machine.definition.devices)hardware.getObjectByName(d.id)?.traverse(o=>{if(o instanceof Mesh&&componentLampKey(o.name)!==undefined)slings.add(d.id)})
  const strokes=machine.definition.devices.flatMap(d=>{
    if(d.kind!=='kicker')return []
    const driven=hardware.getObjectByName(`${d.id}.coilDriven`);if(!(driven instanceof Group))return []
    const h=Math.hypot(...d.exit.direction)
    return [{owner:d.id,driven,scoop:isScoopKicker(d),dx:d.exit.direction[0]/h,dy:d.exit.direction[1]/h,rest:driven.position.clone()}]
  })
  const automatic=machine.definition.devices.flatMap(d=>{const driven=(d.kind==='autoPlunger'||isSecondaryManual(d,machine.definition.devices))?hardware.getObjectByName(d.id+(d.kind==='plunger'?'.manualDriven':'.autoDriven')):undefined;return driven instanceof Group?[{owner:d.id,driven,manual:d.kind==='plunger'}]:[]})
  group.name = 'componentLighting'
  const lensGeometry = new SphereGeometry(1, 12, 6), spillGeometry = new PlaneGeometry(2, 2)
  const lensMaterial = new MeshBasicMaterial({ color: 0xffffff, toneMapped: false,
    transparent: true, blending: AdditiveBlending, opacity: 0.65, depthWrite: false })
  const bytes = new Uint8Array(32 * 32 * 4)
  for (let y = 0; y < 32; y++) for (let x = 0; x < 32; x++) {
    const r = Math.hypot((x + 0.5) / 16 - 1, (y + 0.5) / 16 - 1)
    const v = Math.round(255 * Math.max(0, 1 - r) ** 2), n = (y * 32 + x) * 4
    bytes[n] = bytes[n + 1] = bytes[n + 2] = v; bytes[n + 3] = 255
  }
  const texture = new DataTexture(bytes, 32, 32, RGBAFormat, UnsignedByteType)
  texture.magFilter = texture.minFilter = LinearFilter; texture.needsUpdate = true
  const spillMaterial = new MeshBasicMaterial({ color: 0xffffff, map: texture, transparent: true,
    blending: AdditiveBlending, depthWrite: false, toneMapped: false })
  const bulbs = new InstancedMesh(lensGeometry, lensMaterial, sites.length), pools = new InstancedMesh(spillGeometry, spillMaterial, sites.length)
  bulbs.name = 'componentLighting.lenses'; pools.name = 'componentLighting.spill'
  const matrix = new Matrix4(), colour = new Color()
  for (let i = 0; i < sites.length; i++) {
    const s = sites[i]!
    const bulbRadius = s.role === 'gi' || slings.has(s.owner) ? 0 : s.radius
    matrix.makeScale(bulbRadius, bulbRadius, bulbRadius * 0.28); matrix.setPosition(s.x, s.y, s.z); bulbs.setMatrixAt(i, matrix)
    const radius = (s.role === 'gi' ? s.radius : s.role === 'path' || s.role === 'rail' ? 0.018 : s.radius * 4.4)*(slings.has(s.owner)?.58:1)
    matrix.makeScale(radius, radius, 1); matrix.setPosition(s.x, s.y, s.role === 'path' ? Math.max(0.002, s.z - 0.008) : 0.002)
    pools.setMatrixAt(i, matrix)
    bulbs.setColorAt(i, colour.setRGB(0.12, 0.10, 0.07)); pools.setColorAt(i, colour.setRGB(0, 0, 0))
  }
  bulbs.frustumCulled = pools.frustumCulled = false
  group.add(bulbs, pools)
  const materials: Material[] = [lensMaterial, spillMaterial], bindings: Binding[] = [], caps: CapLens[] = []
  for (const d of machine.definition.devices) {
    if (!['popBumper', 'standupTarget', 'dropTarget', 'spinner', 'slingshot', 'kicker', 'rollover', 'autoPlunger', 'magnet'].includes(d.kind)) continue
    const node = hardware.getObjectByName(d.id)
    node?.traverse(object => {
      if (!(object instanceof Mesh) || !(object.material instanceof MeshStandardMaterial)) return
      const original = object.material
      const physical=object.name.includes('.component.authored.')
      const selected = d.kind==='magnet' ? object.name.endsWith('.component.authored.magnetIndicator') : physical ? componentLampKey(object.name)!==undefined : d.kind === 'slingshot' ? object.name.endsWith('.sling.authored.light') : d.kind === 'popBumper' ? original.metalness <= 0.1 && (original.emissiveIntensity > 0 || original.map !== null || object.geometry.type === 'RingGeometry')
        : d.kind === 'spinner' ? original.emissiveIntensity > 0 : object.name.endsWith('.face') || original.name.includes('HardwareIvory')
      if (!selected || object.name.endsWith('.skirt') || object.name.endsWith('HardwareRubber')) return
      const material = original.clone(); material.name = `componentLighting.${d.id}.${original.name}`
      material.onBeforeCompile=original.onBeforeCompile;material.customProgramCacheKey=original.customProgramCacheKey
      // Preserve printed albedo while allowing the cap's internal lamp to illuminate dark
      // atlas texels, including newly placed Builder parts with no printed motif beneath them.
      const internalCap = d.kind === 'popBumper' && (physical ? componentLampKey(object.name)!==undefined : object.name.endsWith('.cap') || original.map !== null || original.name.includes('HardwareIvory'))
      if (internalCap) material.emissiveMap = null
      else if (material.map !== null) material.emissiveMap = material.map
      if (internalCap && !physical) {
        object.geometry.computeBoundingBox()
        const bounds = object.geometry.boundingBox!, site = sites.findIndex(s => s.owner === d.id && s.role === 'lens')
        caps.push({ mesh: object, site, centre: new Vector3((bounds.min.x + bounds.max.x) / 2, (bounds.min.y + bounds.max.y) / 2, bounds.max.z + 0.0005),
          radius: Math.min(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y) * 0.475 })
      }
      materials.push(material); object.material = material
      bindings.push({ owner: d.id, lamp: d.lamp ?? '', material, original: original.emissive.clone().multiplyScalar(original.emissiveIntensity), gain: d.kind==='magnet'?.65:physical || d.kind === 'slingshot' ? .85 : d.kind === 'popBumper' ? 0.32 : 0.25, mesh: object, source: original, magnet:d.kind==='magnet' })
    })
  }
  let backbox: InstancedMesh | undefined
  if (backglass !== undefined) {
    // Children of the actual backglass use its frame; neither incline nor placement is guessed.
    const material = new MeshBasicMaterial({ color: 0xffffff, toneMapped: false, transparent: true,
      blending: AdditiveBlending, opacity: 0.65, depthWrite: false })
    materials.push(material); backbox = new InstancedMesh(lensGeometry, material, 12)
    backbox.name = 'componentLighting.backbox'; backbox.frustumCulled = false
    for (let i = 0; i < 12; i++) {
      matrix.makeScale(0.005, 0.005, 0.002); matrix.setPosition((i / 11 - 0.5) * backglass.width * 0.9, backglass.height * 0.43, 0.0015)
      backbox.setMatrixAt(i, matrix); backbox.setColorAt(i, colour.setRGB(0.1, 0.08, 0.04))
    }
    backglass.mesh.add(backbox)
  }
  const sample = createLightingSample(), fallback = new ComponentLightingState(machine.definition)
  const inverse = new Matrix4(), relative = new Matrix4()
  let lifted: string | undefined
  const sourceVisible = (owner: string): boolean => {
    if (owner === lifted) return false
    const device = machine.devices.get(owner)
    return device?.kind !== 'dropTarget' || (device as DropTarget).isUp
  }
  const readBase = (lamp: string, read: LampStateReader | undefined, step: number, reduced: boolean): number => lamp === '' || read === undefined ? 0 : lampBrightness(read(lamp), step, 0, reduced)
  const update = (state: ComponentLightingState | undefined, read: LampStateReader | undefined, step: number, reduced: boolean, light?: PointLight): void => {
    if (state === undefined) fallback.advance({ step, attract: true, tilted: false, balls: 0, mode: '' })
    const lighting = state ?? fallback
    for(const s of strokes){
      const stroke=kickerEjectStroke(lighting.step,lighting.ejectedAt(s.owner),s.scoop)
      s.driven.position.set(s.rest.x+(s.scoop?0:s.dx*stroke),s.rest.y+(s.scoop?0:s.dy*stroke),s.rest.z+(s.scoop?stroke:0))
    }
    for(const a of automatic)a.driven.rotation.x=a.manual?manualLauncherAngle(lighting.step,lighting.ejectedAt(a.owner),lighting.ejectedSpeed(a.owner)):autoLauncherAngle(lighting.step,lighting.ejectedAt(a.owner))
    group.updateWorldMatrix(true, false); inverse.copy(group.matrixWorld).invert()
    for (const cap of caps) {
      if (cap.site < 0) continue
      cap.mesh.updateWorldMatrix(true, false); relative.multiplyMatrices(inverse, cap.mesh.matrixWorld)
      matrix.makeScale(cap.radius, cap.radius, 0.001); matrix.setPosition(cap.centre); matrix.premultiply(relative)
      bulbs.setMatrixAt(cap.site, matrix)
    }
    if (caps.length > 0) bulbs.instanceMatrix.needsUpdate = true
    let peak = light?.intensity ?? 0
    for (let i = 0; i < sites.length; i++) {
      const s = sites[i]!
      lighting.sample(s.owner, readBase(s.lamp, read, step, reduced), reduced, sample, s.phase)
      if (!sourceVisible(s.owner)) { sample.level = 0; sample.activity = 0 }
      if (s.role === 'gi' || s.role === 'post' && s.owner === '@ambient') sample.level = 0
      const level = sample.level * (s.role === 'post' ? 0.55 : 0.8)
      colour.setRGB(sample.red * level, sample.green * level, sample.blue * level); bulbs.setColorAt(i, colour)
      const spill = bumpers.has(s.owner)&&slings.has(s.owner) ? .12 : slings.has(s.owner) ? .045 : s.role === 'post' || s.role === 'rail' ? 0.035 : 0.18
      colour.setRGB(sample.red * sample.level * spill, sample.green * sample.level * spill, sample.blue * sample.level * spill); pools.setColorAt(i, colour)
      if (light !== undefined && s.role === 'lens' && sample.activity * 0.012 > peak) {
        peak = sample.activity * 0.012; light.intensity = peak; light.distance = 0.16
        light.position.set(s.x, s.y, s.z + 0.025); light.color.setRGB(sample.red, sample.green, sample.blue)
      }
    }
    bulbs.instanceColor!.needsUpdate = pools.instanceColor!.needsUpdate = true
    for (const b of bindings) {
      lighting.sample(b.owner, readBase(b.lamp, read, step, reduced), reduced, sample)
      if(b.magnet){const device=machine.devices.get(b.owner) as Magnet|undefined;sample.level=device?.fieldOn&&device.enabled?1:0;sample.red=.1;sample.green=.75;sample.blue=1}
      if (!sourceVisible(b.owner)) sample.level = 0
      b.material.emissive.setRGB((sample.red * b.gain + b.original.r) * sample.level,
        (sample.green * b.gain + b.original.g) * sample.level, (sample.blue * b.gain + b.original.b) * sample.level)
      b.material.emissiveIntensity = 1
    }
    if (backbox !== undefined) {
      for (let i = 0; i < 12; i++) { lighting.sample('@ambient', 0, reduced, sample, i / 11); backbox.setColorAt(i, colour.setRGB(sample.red * sample.level, sample.green * sample.level, sample.blue * sample.level)) }
      backbox.instanceColor!.needsUpdate = true
    }
  }
  return { group, sites, materials, geometries: [lensGeometry, spillGeometry], texture, update,
    lift: id => {
      lifted = id
      for (const b of bindings) if (b.mesh.parent === group) b.mesh.visible = b.owner !== id
      for (let i = 0; i < sites.length; i++) if (sites[i]!.owner === id) { bulbs.setColorAt(i, colour.setRGB(0, 0, 0)); pools.setColorAt(i, colour) }
      bulbs.instanceColor!.needsUpdate = pools.instanceColor!.needsUpdate = true
    },
    dispose: () => {
      for (const b of bindings) if (b.mesh.material === b.material) b.mesh.material = b.source
      backbox?.removeFromParent(); backbox?.dispose()
      group.removeFromParent(); bulbs.dispose(); pools.dispose()
      group.clear()
      for (const m of materials) m.dispose()
      lensGeometry.dispose(); spillGeometry.dispose(); texture.dispose()
    } }
}
